// src/app/redux/sagas/offboardingSaga.js
import { call, put, select } from "redux-saga/effects";
import Swal from "sweetalert2";
import { API_BASE_URL, getRequest, postRequest, putRequest, deleteRequest } from "../../../api";

import {
    setOffboardingLoading,

    fetchOffboardingsSuccess,
    fetchOffboardingsFail,
    getOffboardingRequest,
    getOffboardingSuccess,
    getOffboardingFail,

    createOffboardingSuccess,
    createOffboardingFail,

    updateOffboardingSuccess,
    updateOffboardingFail,

    deleteOffboardingSuccess,
    deleteOffboardingFail,
    fetchEtapesRequest,
    fetchEtapesSuccess,
    fetchEtapesFail,

    createEtapeSuccess,
    createEtapeFail,

    updateEtapeSuccess,
    updateEtapeFail,

    deleteEtapeSuccess,
    deleteEtapeFail,
} from "../actions/offboardingActions";

/* =========================
   HELPERS
========================= */
const extractErrorMessage = (error, fallback) => {
    let msg = fallback;
    if (error?.response?.data?.message) msg = error.response.data.message;
    else if (error?.response?.data?.error) msg = error.response.data.error;
    else if (error?.message) msg = error.message;
    return msg;
};

// ✅ IMPORTANT: api.js fait déjà `Bearer ${data.token}`
// Donc ici on renvoie TOUJOURS le token "nu"
const getToken = () => {
    const raw = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
    if (!raw) return "";
    return raw.startsWith("Bearer ") ? raw.replace(/^Bearer\s+/i, "") : raw;
};

/* =========================
   OFFBOARDINGS
========================= */
export function* fetchOffboardingsSaga() {
    const token = getToken();
    const url = `${API_BASE_URL}/api/offbord/offboardings`;

    yield put(setOffboardingLoading(true));
    try {
        // ✅ NE PAS passer headers ici (api.js lit uid depuis sessionStorage)
        const resp = yield call(getRequest, { url, token });
        yield put(fetchOffboardingsSuccess(resp.data || []));
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur chargement offboardings");
        yield put(fetchOffboardingsFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", text: msg, timer: 2000, showConfirmButton: false });
    } finally {
        yield put(setOffboardingLoading(false));
    }
}

export function* getOffboardingSaga(action) {
    const token = getToken();
    const id = action.payload;
    const url = `${API_BASE_URL}/api/offbord/offboardings/${id}`;

    yield put(setOffboardingLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(getOffboardingSuccess(resp.data));
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur chargement offboarding");
        yield put(getOffboardingFail(msg));
    } finally {
        yield put(setOffboardingLoading(false));
    }
}

export function* createOffboardingSaga(action) {
    const token = getToken();
    const url = `${API_BASE_URL}/api/offbord/offboardings`;
    const data = action.payload;

    yield put(setOffboardingLoading(true));
    try {
        const resp = yield call(postRequest, { url, data, token });
        yield put(createOffboardingSuccess(resp.data));

        yield call([Swal, Swal.fire], {
            icon: "success",
            text: "Offboarding créé",
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur création offboarding");
        yield put(createOffboardingFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", title: "Erreur", text: msg });
    } finally {
        yield put(setOffboardingLoading(false));
    }
}

export function* updateOffboardingSaga(action) {
    const token = getToken();
    const { id, payload } = action.payload;
    const url = `${API_BASE_URL}/api/offbord/offboardings/${id}`;

    // optimistic (ok)
    yield put(updateOffboardingSuccess({ id, ...payload }));

    try {
        // ✅ prendre l’objet complet du store
        const state = yield select((s) => s.offboarding || {});
        const current =
            state.selectedOffboarding?.id === id
                ? state.selectedOffboarding
                : (state.offboardings || []).find((o) => String(o.id) === String(id));

        const dataToSend = { ...(current || {}), ...payload };

        const resp = yield call(putRequest, { url, data: dataToSend, token });

        // ✅ après update : recharger depuis backend pour être sûr
        // (ça permet aussi de voir si backend a vraiment persisté)
        yield put(getOffboardingRequest(id));

    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur mise à jour offboarding");
        yield put(updateOffboardingFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", title: "Offboarding non sauvegardé", text: msg });
    }
}

export function* deleteOffboardingSaga(action) {
    const token = getToken();
    const id = action.payload;
    const url = `${API_BASE_URL}/api/offbord/offboardings/${id}`;

    const confirm = yield call([Swal, Swal.fire], {
        icon: "warning",
        title: "Supprimer ?",
        text: "L'offboarding et ses étapes seront supprimés.",
        showCancelButton: true,
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#d33",
    });

    if (!confirm?.isConfirmed) return;

    yield put(setOffboardingLoading(true));
    try {
        yield call(deleteRequest, { url, token });
        yield put(deleteOffboardingSuccess(id));
        yield call([Swal, Swal.fire], { icon: "success", text: "Supprimé", timer: 1200, showConfirmButton: false });
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur suppression offboarding");
        yield put(deleteOffboardingFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", title: "Erreur", text: msg });
    } finally {
        yield put(setOffboardingLoading(false));
    }
}

/* =========================
   ETAPES
========================= */
export function* fetchEtapesSaga(action) {
    const token = getToken();
    const offboardingId = action.payload;
    const url = `${API_BASE_URL}/api/offbord/offboardings/${offboardingId}/etapes`;

    yield put(setOffboardingLoading(true));
    try {
        const resp = yield call(getRequest, { url, token });
        yield put(fetchEtapesSuccess(offboardingId, resp.data || []));
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur chargement étapes");
        yield put(fetchEtapesFail(msg));
    } finally {
        yield put(setOffboardingLoading(false));
    }
}

export function* createEtapeSaga(action) {
    const token = getToken();
    const url = `${API_BASE_URL}/api/offbord/offboarding-etapes`;
    const data = action.payload;

    yield put(setOffboardingLoading(true));
    try {
        const resp = yield call(postRequest, { url, data, token });
        yield put(createEtapeSuccess(resp.data));
        yield call([Swal, Swal.fire], { icon: "success", text: "Étape ajoutée", timer: 1200, showConfirmButton: false });
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur ajout étape");
        yield put(createEtapeFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", title: "Erreur", text: msg });
    } finally {
        yield put(setOffboardingLoading(false));
    }
}

export function* updateEtapeSaga(action) {
    const token = getToken();
    const { id, payload } = action.payload;
    const url = `${API_BASE_URL}/api/offbord/offboarding-etapes/${id}`;

    // optimistic (ok)
    yield put(updateEtapeSuccess({ id, ...payload }));

    try {
        const state = yield select((s) => s.offboarding || {});
        const etapesByOffboarding = state.etapesByOffboarding || {};

        // retrouver l’étape actuelle dans le store
        let currentEtape = null;
        let offboardingIdFound = null;

        for (const [offId, list] of Object.entries(etapesByOffboarding)) {
            const found = (list || []).find((e) => String(e.id) === String(id));
            if (found) {
                currentEtape = found;
                offboardingIdFound = Number(offId);
                break;
            }
        }

        const dataToSend = { ...(currentEtape || {}), ...payload };

        // ✅ si ton backend a besoin de offboardingId et qu’il n’est pas dans l’objet
        if (!dataToSend.offboardingId && offboardingIdFound) {
            dataToSend.offboardingId = offboardingIdFound;
        }

        const resp = yield call(putRequest, { url, data: dataToSend, token });

        // ✅ refetch liste après update pour être sûr que DB a changé
        if (offboardingIdFound) {
            yield put(fetchEtapesRequest(offboardingIdFound));
        }

    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur mise à jour étape");
        yield put(updateEtapeFail(msg));
        yield call([Swal, Swal.fire], { icon: "error", title: "Étape non sauvegardée", text: msg });
    }
}

export function* deleteEtapeSaga(action) {
    const token = getToken();
    const { id, offboardingId } = action.payload;
    const url = `${API_BASE_URL}/api/offbord/offboarding-etapes/${id}`;

    const confirm = yield call([Swal, Swal.fire], {
        icon: "warning",
        title: "Supprimer l'étape ?",
        text: "Cette action est irréversible.",
        showCancelButton: true,
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#d33",
    });

    if (!confirm?.isConfirmed) return;

    yield put(setOffboardingLoading(true));
    try {
        yield call(deleteRequest, { url, token });
        yield put(deleteEtapeSuccess(id, offboardingId));
    } catch (error) {
        const msg = extractErrorMessage(error, "Erreur suppression étape");
        yield put(deleteEtapeFail(msg));
    } finally {
        yield put(setOffboardingLoading(false));
    }
}
